import { useEffect, useRef } from "react";
import useSearch from "@/hooks/useSearch";
import { logger } from "@/lib/logger";
import {
  buildSourceSearchUrl,
  shouldSaveSourceSearch,
  type SourceSearchTableState,
} from "@/lib/search/sourceSearch";

/**
 * Stores the Kilder table's search when the user leaves the page, so that a
 * source search reaches recent searches and can be favorited from there
 * (LAR-422).
 *
 * Leaving the page is the counterpart of closing the search modal, which is
 * when a global search is stored. Storing on every keystroke instead would
 * bury the user's real searches under every prefix they typed.
 */
export const useSaveSourceSearchOnLeave = (
  state: SourceSearchTableState
): void => {
  const { createSearch } = useSearch();

  const visitStartedWithRef = useRef<SourceSearchTableState>(state);
  const latestStateRef = useRef<SourceSearchTableState>(state);
  const saveRef = useRef(createSearch.mutate);

  // Mirrors of the latest render, read by the unmount cleanup below.
  useEffect(() => {
    latestStateRef.current = state;
    saveRef.current = createSearch.mutate;
  });

  useEffect(
    () => () => {
      const current = latestStateRef.current;
      if (!shouldSaveSourceSearch(visitStartedWithRef.current, current)) return;

      saveRef.current(
        { searchUrl: buildSourceSearchUrl(current) },
        {
          onError: (error: unknown) => {
            logger.error("Failed to store source search", {
              operation: "saveSourceSearchOnLeave",
              error,
              searchTerm: current.search,
              searchTagCount: current.searchTags.length,
            });
          },
        }
      );
    },
    []
  );
};
